import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IconShoppingCartPlus, IconCircle, IconCircleFilled } from "@tabler/icons-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  ToggleGroup,
  ToggleGroupItem,
  Badge,
  Button,
  Label,
  cn,
  useIsMobile,
} from "@lemonadetraining/web-ui";
import type { PaymentMode } from "@lemonadetraining/contracts";
import { cartActions } from "@lemonadetraining/web-shared";
import { describeCommitmentOption } from "@lemonadetraining/web-shared/utils/membershipLabels";
import {
  COMMITMENT_STOREFRONT_COPY,
  availablePaymentModes,
  bestValuePlanKey,
  buildMembershipCartLine,
  commitmentFooterText,
  isPaymentMode,
  optionInstalment,
  visiblePlanEntries,
  type PlanOption,
} from "@lemonadetraining/web-shared/utils/commitmentPlans";
import { useAppDispatch, useAppSelector } from "../store/hooks";

/**
 * The option shape this popover renders. Identical to — and now simply — the
 * storefront's own `PlanOption`: one declaration, so a field added to the
 * pricing cell cannot reach the util and miss the component.
 */
export type PricingOption = PlanOption;

export interface PlanSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: Record<string, PricingOption>;
  productName: string;
  productId: string;
  coachName: string;
  coachUID: string;
  coverArtURL?: string;
  stripeAccountID?: string | null;
  isLemonadeProduct?: boolean;
  /**
   * The gym selling this membership. Travels into the cart line so checkout can
   * re-read the product's current terms before asking anyone to commit to them.
   */
  boxID?: string;
  onSuccess?: () => void;
  brandColor?: string;
}

function formatPlanLabel(option: PricingOption): { name: string; total: string; perUnit: string } {
  // Visit-card vs recurring semantics (which field is the quantity, the per-unit
  // divisor, pluralization) and the commitment term come from the shared
  // formatter; this stays as the marketing site's presentation (EUR token,
  // "/mo" suffix). A non-commitment option reads exactly as it always has.
  const { isVisitCard, unitLabel, totalAmount, perUnitAmount } = describeCommitmentOption(option);
  return {
    name: unitLabel,
    total: `EUR ${totalAmount.toFixed(2)}`,
    perUnit: `EUR ${perUnitAmount.toFixed(2)}/${isVisitCard ? "visit" : "mo"}`,
  };
}

function PlanSelectorContent({
  open,
  options,
  productName,
  productId,
  coachName,
  coachUID,
  coverArtURL,
  stripeAccountID,
  isLemonadeProduct,
  boxID,
  onOpenChange,
  onSuccess,
  titleId,
  brandColor,
}: PlanSelectorProps & { titleId: string; brandColor?: string }) {
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [selectedMode, setSelectedMode] = useState<PaymentMode | null>(null);
  // Empty until the shopper actually switches lanes: a live region that mounts
  // with text in it can be read out on open, which would announce a choice
  // nobody made.
  const [laneAnnouncement, setLaneAnnouncement] = useState<string>("");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAppSelector((state) => state.user);
  const firstRadioRef = useRef<HTMLButtonElement>(null);

  // The payment mode is a second dimension over the length rows, and it exists
  // only for a gym selling commitment terms. Without one there is no mode at
  // all (`null`), which keeps a grandfathered product's popover exactly as it
  // was: same rows, same order, no extra control.
  const paymentModes = availablePaymentModes(options);
  const activeMode = paymentModes.length > 0 ? (selectedMode ?? paymentModes[0]) : null;
  // One mode on offer is information, not a choice — a two-way control with an
  // empty side would invite a click that can do nothing. The honesty footer
  // still names how the selected term is paid.
  const showModeToggle = paymentModes.length > 1;

  const activeOptions = visiblePlanEntries(options, activeMode);
  const bestValueKey = bestValuePlanKey(activeOptions);
  const selectedOption = selectedKey ? options[selectedKey] : null;
  const commitmentFooter = commitmentFooterText(selectedOption);

  // Reset selection when reopened to prevent stale state
  useEffect(() => {
    if (open) {
      setSelectedKey("");
      setSelectedMode(null);
      setLaneAnnouncement("");
    }
  }, [open]);

  const handleModeChange = useCallback((next: string) => {
    // Radix reports "" when the pressed item is pressed again. There is no
    // "neither mode" state to fall into, so that deselection is ignored.
    if (!isPaymentMode(next)) return;
    setSelectedMode(next);
    // The chosen length may not exist in the other lane, and a selection that
    // survived invisibly would add a plan the shopper can no longer see.
    setSelectedKey("");
    // Every row on screen was just re-priced and the selection dropped. That is
    // obvious to anyone looking at it and silent to anyone who is not.
    setLaneAnnouncement(COMMITMENT_STOREFRONT_COPY.paymentModeAnnouncements[next]);
  }, []);

  // Auto-focus first radio item on open
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      firstRadioRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [open]);

  const handleConfirm = useCallback(() => {
    if (!selectedOption || !selectedKey) return;

    if (!user.isLoggedIn) {
      toast("Sign in to add items to your cart");
      sessionStorage.setItem("lemonade_landing_auth_redirect", "true");
      navigate("/login", { state: { from: pathname } });
      return;
    }

    dispatch(
      cartActions.addToCart(
        buildMembershipCartLine(
          {
            productId,
            name: productName,
            coachName,
            coachUID,
            coverArt: coverArtURL,
            stripeAccountID,
            isLemonadeProduct,
            boxID,
          },
          selectedOption,
          selectedKey
        )
      )
    );

    toast.success("Added to cart!");
    onOpenChange(false);
    onSuccess?.();
  }, [
    selectedOption,
    selectedKey,
    user,
    navigate,
    pathname,
    dispatch,
    productName,
    productId,
    coachName,
    coachUID,
    coverArtURL,
    stripeAccountID,
    isLemonadeProduct,
    boxID,
    onOpenChange,
    onSuccess,
  ]);

  // The button names what pressing it will charge. For a monthly-billed term
  // that is the month's price, not the term total — the same figure the row
  // above it shows as the per-month rate, and the same one the footer's total
  // is built from.
  const confirmInstalment = selectedOption ? optionInstalment(selectedOption) : null;
  const confirmLabel = confirmInstalment
    ? `Add to Cart — EUR ${confirmInstalment.amount.toFixed(2)}${confirmInstalment.perPeriodSuffix}`
    : "Add to Cart";

  return (
    <div data-testid="plan-selector" role="group" aria-labelledby={titleId}>
      {showModeToggle && (
        <ToggleGroup
          type="single"
          value={activeMode ?? ""}
          onValueChange={handleModeChange}
          variant="outline"
          aria-label={COMMITMENT_STOREFRONT_COPY.paymentModeLabel}
          className="mb-3 grid w-full grid-cols-2"
          data-testid="payment-mode-toggle"
        >
          {paymentModes.map((mode) => {
            const isSelected = activeMode === mode;
            // The pressed segment differs by a filled-vs-hollow marker, not by
            // its background alone — the colour is the reinforcement, never the
            // only carrier of which mode is on.
            const Marker = isSelected ? IconCircleFilled : IconCircle;
            return (
              <ToggleGroupItem
                key={mode}
                value={mode}
                className="min-h-[44px]"
                data-testid={`payment-mode-${mode}`}
              >
                <Marker size={14} aria-hidden="true" />
                {COMMITMENT_STOREFRONT_COPY.paymentModeLabels[mode]}
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      )}

      {/* Mounted with the toggle and empty until a lane is actually switched, so
          the change announces itself. The prices and the cleared selection are
          the visible half of that event; this is the other half. */}
      {showModeToggle && (
        <span className="sr-only" aria-live="polite" data-testid="payment-mode-announcement">
          {laneAnnouncement}
        </span>
      )}

      <RadioGroup value={selectedKey} onValueChange={setSelectedKey} className="gap-3">
        {activeOptions.map(([key, option], index) => {
          const labels = formatPlanLabel(option);
          const isBestValue = key === bestValueKey;

          return (
            <div key={key} data-testid={`plan-option-${key}`}>
              <Label
                htmlFor={`plan-${key}`}
                className={cn(
                  "flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                  // Hover is scoped to the rows you could still pick: `hover:` outranks a
                  // plain background class, so an unscoped hover tint would lighten the
                  // chosen row on pointer-over and invert which one reads as selected.
                  selectedKey === key ? "border-ring bg-accent/20" : "hover:bg-accent/10"
                )}
              >
                <RadioGroupItem
                  ref={index === 0 ? firstRadioRef : undefined}
                  value={key}
                  id={`plan-${key}`}
                />
                <div className="flex flex-1 items-center justify-between">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium">{labels.name}</span>
                    <span className="text-xs text-muted-foreground">{labels.perUnit}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isBestValue && activeOptions.length > 1 && (
                      <Badge variant="success" data-testid="best-value-badge">
                        Best Value
                      </Badge>
                    )}
                    <span className="text-sm font-semibold">{labels.total}</span>
                  </div>
                </div>
              </Label>
            </div>
          );
        })}
      </RadioGroup>

      {/* Mounted for the whole time a commitment product is open — a live
          region has to exist before its content arrives to be announced — and
          not at all for a product that sells no commitment, whose popover is
          unchanged from before commitments existed. */}
      {activeMode !== null && (
        <p
          className="text-muted-foreground mt-3 text-xs empty:mt-0"
          aria-live="polite"
          data-testid="commitment-footer"
        >
          {commitmentFooter}
        </p>
      )}

      <Button
        className="w-full min-h-[44px] mt-4"
        style={brandColor ? { backgroundColor: brandColor, color: "white" } : undefined}
        onClick={handleConfirm}
        disabled={!selectedKey}
        data-testid="plan-confirm"
      >
        <IconShoppingCartPlus size={18} className="mr-2" />
        {confirmLabel}
      </Button>
    </div>
  );
}

function PlanSelector(props: PlanSelectorProps) {
  const { open, onOpenChange } = props;
  const isMobile = useIsMobile();
  const titleId = "plan-selector-title";

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          showCloseButton={true}
          className="rounded-t-xl"
          aria-labelledby={titleId}
        >
          <SheetHeader>
            <SheetTitle id={titleId}>Choose Your Plan</SheetTitle>
            <SheetDescription className="sr-only">
              Select a pricing plan for {props.productName}
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-4">
            <PlanSelectorContent {...props} titleId={titleId} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <span />
      </PopoverTrigger>
      <PopoverContent className="w-80" align="center" sideOffset={8} aria-labelledby={titleId}>
        <PopoverHeader>
          <PopoverTitle id={titleId}>Choose Your Plan</PopoverTitle>
          <PopoverDescription className="sr-only">
            Select a pricing plan for {props.productName}
          </PopoverDescription>
        </PopoverHeader>
        <PlanSelectorContent {...props} titleId={titleId} />
      </PopoverContent>
    </Popover>
  );
}

export default PlanSelector;
